import { useState, useEffect } from 'react';
import contractorsApi from '../api/contractorsApi.js';
import './TutorManagement.css';

const TutorManagement = () => {
  const [tutors, setTutors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [earningsData, setEarningsData] = useState({});
  const [, setEarningsLoadingOverall] = useState(false);
  const [, setEarningsProgress] = useState({ current: 0, total: 0 });
  const [selectedYear] = useState(new Date().getFullYear());
  
  // Helper function to format currency with commas
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // New state for tutor management
  const [showSignupForm, setShowSignupForm] = useState(false);
  // Google Groups state
  const [availableGroups, setAvailableGroups] = useState({});
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [selectedTutorForGroups, setSelectedTutorForGroups] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState(null);

  // Branch ID Manager state
  const [contractorsWithBranchIds, setContractorsWithBranchIds] = useState([]);
  const [contractorsWithoutBranchIds, setContractorsWithoutBranchIds] = useState([]);
  const [, setBranchLoading] = useState(false);
  const [branchSearchTerm, setBranchSearchTerm] = useState('');
  const [branchSearchResults, setBranchSearchResults] = useState([]);
  const [selectedContractorForBranch, setSelectedContractorForBranch] = useState(null);
  const [newBranchId, setNewBranchId] = useState('');
  const [branchMessage, setBranchMessage] = useState('');
  const [showBranchIdModal, setShowBranchIdModal] = useState(false);

  // Signup form state
  const [signupForm, setSignupForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    employmentType: '1099', // 'w2' or '1099'
    hourlyRate: '',
    subjects: [],
    availability: {
      weekdays: false,
      weekends: false,
      evenings: false,
      mornings: false
    },
    experience: '',
    education: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    taxInfo: {
      ssn: '',
      w9OnFile: false
    },
    onboardingPreferences: {
      googleGroups: true,
      storyTimeTutors: true,
      branchPayments: true,
      tutorCruncher: true
    }
  });

  useEffect(() => {
    fetchTutors();
    fetchStats();
    fetchAvailableGroups();
    fetchContractorsWithBranchIds();
    fetchContractorsWithoutBranchIds();
  }, [filter, statusFilter]);

  useEffect(() => {
    if (tutors.length > 0) {
      fetchAllEarnings();
    }
  }, [tutors, selectedYear]);

  const fetchTutors = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (filter !== 'all') {
        filters.employment_type = filter;
      }
      
      if (statusFilter !== 'all') {
        filters.is_active = statusFilter === 'active';
      }
      
      const response = await contractorsApi.getContractors(filters);
      setTutors(response.data || []);
    } catch (err) {
      setError('Failed to fetch tutors');
      console.error('Error fetching tutors:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await contractorsApi.getContractorStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchAllEarnings = async () => {
    try {
      setEarningsLoadingOverall(true);
      setEarningsProgress({ current: 0, total: tutors.length });
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/contractors/earnings/bulk?year=${selectedYear}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        const bulkEarningsData = result.data;
        
        const newEarningsData = {};
        tutors.forEach(tutor => {
          const tutorId = tutor.contractorId;
          if (bulkEarningsData[tutorId]) {
            newEarningsData[tutorId] = bulkEarningsData[tutorId];
          } else {
            newEarningsData[tutorId] = {
              contractorId: tutorId,
              totalEarnings: 0,
              paidEarnings: 0,
              pendingEarnings: 0,
              totalOrders: 0,
              monthlyEarnings: {},
              paymentOrders: []
            };
          }
        });
        
        setEarningsData(newEarningsData);
      }
    } catch (err) {
      console.error('Error fetching earnings:', err);
    } finally {
      setEarningsLoadingOverall(false);
    }
  };

  const getEmploymentTypeLabel = (type) => {
    switch (type) {
      case 'w2': return 'W2 Employee';
      case '1099': return '1099 Tutor';
      default: return 'Unclassified';
    }
  };

  const getEmploymentTypeColor = (type) => {
    switch (type) {
      case 'w2': return '#007bff';
      case '1099': return '#28a745';
      default: return '#6c757d';
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Submit tutor signup
      const response = await fetch('/api/tutors/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(signupForm)
      });

      if (response.ok) {
        await response.json();
        alert('Tutor signup submitted successfully! Onboarding process will begin.');
        setShowSignupForm(false);
        setSignupForm({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: { street: '', city: '', state: '', zipCode: '', country: 'US' },
          employmentType: '1099',
          hourlyRate: '',
          subjects: [],
          availability: { weekdays: false, weekends: false, evenings: false, mornings: false },
          experience: '',
          education: '',
          emergencyContact: { name: '', phone: '', relationship: '' },
          taxInfo: { ssn: '', w9OnFile: false },
          onboardingPreferences: { googleGroups: true, storyTimeTutors: true, branchPayments: true, tutorCruncher: true }
        });
        fetchTutors(); // Refresh the list
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error || 'Failed to submit signup'}`);
      }
    } catch (err) {
      console.error('Error submitting signup:', err);
      alert('Error submitting signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  // WordPress-specific handlers
  const [wordpressStatus, setWordpressStatus] = useState({});

  const handleCreateWordPressUser = async (tutorId) => {
    try {
      setWordpressStatus({ ...wordpressStatus, [tutorId]: 'creating' });
      
      const response = await fetch('/api/tutors/wordpress/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ tutorId })
      });

      if (response.ok) {
        const result = await response.json();
        setWordpressStatus({ ...wordpressStatus, [tutorId]: 'created' });
        
        // Show success message with temporary password
        if (result.data && result.data.temporaryPassword) {
          alert(`WordPress user created successfully!\n\nTemporary Password: ${result.data.temporaryPassword}\n\nPlease share this password with the tutor and ask them to change it immediately.`);
        } else {
          alert('WordPress user created successfully!');
        }
        
        fetchTutors(); // Refresh the list
      } else {
        const errorData = await response.json();
        setWordpressStatus({ ...wordpressStatus, [tutorId]: 'failed' });
        alert(`WordPress user creation failed: ${errorData.error}`);
      }
    } catch (err) {
      console.error('Error creating WordPress user:', err);
      setWordpressStatus({ ...wordpressStatus, [tutorId]: 'failed' });
      alert('Error creating WordPress user. Please try again.');
    }
  };

  const handleDeleteWordPressUser = async (tutorId) => {
    if (!window.confirm('Are you sure you want to delete this WordPress user? This action cannot be undone.')) {
      return;
    }

    try {
      setWordpressStatus({ ...wordpressStatus, [tutorId]: 'deleting' });
      
      const response = await fetch('/api/tutors/wordpress/delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ tutorId })
      });

      if (response.ok) {
        await response.json();
        setWordpressStatus({ ...wordpressStatus, [tutorId]: 'deleted' });
        alert('WordPress user deleted successfully!');
        fetchTutors(); // Refresh the list
      } else {
        const errorData = await response.json();
        setWordpressStatus({ ...wordpressStatus, [tutorId]: 'failed' });
        alert(`WordPress user deletion failed: ${errorData.error}`);
      }
    } catch (err) {
      console.error('Error deleting WordPress user:', err);
      setWordpressStatus({ ...wordpressStatus, [tutorId]: 'failed' });
      alert('Error deleting WordPress user. Please try again.');
    }
  };



  // Google Groups functions
  const fetchAvailableGroups = async () => {
    try {
      const response = await fetch('/api/google-groups/available-groups', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setAvailableGroups(result.groups);
      }
    } catch (err) {
      console.error('Error fetching available groups:', err);
    }
  };

  const handleManageGroups = (tutor) => {
    setSelectedTutorForGroups(tutor);
    setShowGroupsModal(true);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const updateTutorMemberships = (tutorEmail, memberships) => {
    // Update selectedTutorForGroups in-place so the modal reflects changes immediately
    setSelectedTutorForGroups(prev => prev ? { ...prev, googleGroupsMemberships: memberships } : prev);
    // Update the tutor in the main list so it persists when modal closes
    setTutors(prev => prev.map(t =>
      t.email === tutorEmail ? { ...t, googleGroupsMemberships: memberships, googleGroupsAccess: memberships.length > 0 } : t
    ));
  };

  const handleAddToGroups = async (tutorEmail, groupKeys) => {
    try {
      setGroupsLoading(true);
      const response = await fetch(`/api/google-groups/tutors/${tutorEmail}/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ groups: groupKeys })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.memberships) {
          updateTutorMemberships(tutorEmail, result.memberships);
        }
        showToast(`Added to ${groupKeys.length} group(s)`);
      } else {
        const errorData = await response.json();
        showToast(`Failed to add to groups: ${errorData.error}`, 'error');
      }
    } catch (err) {
      console.error('Error adding to groups:', err);
      showToast('Error adding to groups. Please try again.', 'error');
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleRemoveFromGroups = async (tutorEmail, groupKeys) => {
    try {
      setGroupsLoading(true);
      const response = await fetch(`/api/google-groups/tutors/${tutorEmail}/groups`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ groups: groupKeys })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.memberships) {
          updateTutorMemberships(tutorEmail, result.memberships);
        }
        showToast(`Removed from ${groupKeys.length} group(s)`);
      } else {
        const errorData = await response.json();
        showToast(`Failed to remove from groups: ${errorData.error}`, 'error');
      }
    } catch (err) {
      console.error('Error removing from groups:', err);
      showToast('Error removing from groups. Please try again.', 'error');
    } finally {
      setGroupsLoading(false);
    }
  };

  // Branch ID Manager functions
  const fetchContractorsWithBranchIds = async () => {
    setBranchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/branch-ids/with-ids', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContractorsWithBranchIds(data.contractors);
      }
    } catch (error) {
      console.error('Error fetching contractors with Branch IDs:', error);
    } finally {
      setBranchLoading(false);
    }
  };

  const fetchContractorsWithoutBranchIds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/branch-ids/without-ids', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContractorsWithoutBranchIds(data.contractors);
      }
    } catch (error) {
      console.error('Error fetching contractors without Branch IDs:', error);
    }
  };

  const searchContractorsForBranch = async () => {
    if (!branchSearchTerm.trim()) {
      setBranchSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/branch-ids/search?term=${encodeURIComponent(branchSearchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBranchSearchResults(data.contractors);
      }
    } catch (error) {
      console.error('Error searching contractors:', error);
    }
  };

  const addBranchId = async () => {
    if (!selectedContractorForBranch || !newBranchId.trim()) {
      setBranchMessage('Please select a tutor and enter a Branch ID');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/branch-ids/add', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractorId: selectedContractorForBranch.contractorId,
          branchWorkerId: newBranchId.trim()
        })
      });
      
      if (response.ok) {
        setBranchMessage('Branch ID added successfully!');
        setNewBranchId('');
        setSelectedContractorForBranch(null);
        fetchContractorsWithBranchIds();
        fetchContractorsWithoutBranchIds();
        fetchTutors(); // Refresh the main tutors list
      } else {
        const error = await response.json();
        setBranchMessage(error.error || 'Failed to add Branch ID');
      }
    } catch (error) {
      console.error('Error adding Branch ID:', error);
      setBranchMessage('Error adding Branch ID');
    }
  };

  const generateMissingIdsCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/branch-ids/generate-csv', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'contractors-missing-branch-ids.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        setBranchMessage('CSV downloaded successfully!');
      }
    } catch (error) {
      console.error('Error generating CSV:', error);
      setBranchMessage('Error generating CSV');
    }
  };

  const generateBranchWorkerId = () => {
    const generateHex = (length) => {
      return Array.from({length}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    };
    
    // Generate a Branch Worker ID in the format: 8 characters + 4 characters + 4 characters + 4 characters + 12 characters
    const id = `${generateHex(8)}-${generateHex(4)}-${generateHex(4)}-${generateHex(4)}-${generateHex(12)}`;
    setNewBranchId(id);
  };

  const filteredTutors = tutors.filter(tutor =>
    (filter === 'all' || tutor.employment_type === filter) &&
    (statusFilter === 'all' || (statusFilter === 'active' ? tutor.is_active : !tutor.is_active)) &&
    (searchTerm === '' || 
      tutor.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tutor.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tutor.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="tutors-container">
        <div className="loading">Loading tutors...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tutors-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchTutors}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Tutor Management Dashboard</h1>
          <p className="text-gray-600 text-sm">Manage tutors and their information</p>
        </div>
        <button 
          onClick={() => {
            setShowSignupForm(true);
          }}
          className="bg-primary text-white px-4 py-2 rounded-md font-medium hover:bg-primary/90 transition-colors"
        >
          ➕ Add New Tutor
        </button>
      </div>

      {/* Stats Section */}
      {stats && stats.employment_types && (
        <div className="stats-section">
          <h3>System Overview</h3>
          <div className="stats-grid">
            {stats.employment_types.map(stat => (
              <div key={stat._id} className="stat-card">
                <div className="stat-number">{stat.count}</div>
                <div className="stat-label">
                  {stat._id === 'w2' ? 'W2 Employees' : 
                   stat._id === '1099' ? '1099 Tutors' : 
                   'Unclassified'}
                </div>
              </div>
            ))}
            {stats.total && (
              <div className="stat-card">
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Total Tutors</div>
              </div>
            )}
            {stats.active && (
              <div className="stat-card">
                <div className="stat-number">{stats.active}</div>
                <div className="stat-label">Active Tutors</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-group">
          <h4>Employment Type</h4>
          <div className="filter-buttons">
            <button 
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              All ({tutors.length})
            </button>
            <button 
              className={filter === 'w2' ? 'active' : ''}
              onClick={() => setFilter('w2')}
            >
              W2 Employees ({tutors.filter(c => c.employment_type === 'w2').length})
            </button>
            <button 
              className={filter === '1099' ? 'active' : ''}
              onClick={() => setFilter('1099')}
            >
              1099 Tutors ({tutors.filter(c => c.employment_type === '1099').length})
            </button>
            <button 
              className={filter === 'unknown' ? 'active' : ''}
              onClick={() => setFilter('unknown')}
            >
              Unclassified ({tutors.filter(c => c.employment_type === 'unknown').length})
            </button>
          </div>
        </div>

        <div className="filter-group">
          <h4>Status</h4>
          <div className="filter-buttons">
            <button 
              className={statusFilter === 'all' ? 'active' : ''}
              onClick={() => setStatusFilter('all')}
            >
              All Status ({tutors.length})
            </button>
            <button 
              className={statusFilter === 'active' ? 'active' : ''}
              onClick={() => setStatusFilter('active')}
            >
              Active ({tutors.filter(c => c.is_active).length})
            </button>
            <button 
              className={statusFilter === 'inactive' ? 'active' : ''}
              onClick={() => setStatusFilter('inactive')}
            >
              Inactive ({tutors.filter(c => !c.is_active).length})
            </button>
          </div>
        </div>

        <div className="search-section">
          <input
            type="text"
            placeholder="Search tutors by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Tutors List */}
      <div className="tutors-list">
        {filteredTutors.length === 0 ? (
          <div className="no-results">
            No tutors found matching your criteria.
          </div>
        ) : (
          filteredTutors.map(tutor => (
            <div key={tutor.contractorId} className="tutor-card">
              <div className="tutor-info">
                <div className="tutor-header">
                  <h3>
                    {tutor.firstName && tutor.lastName ? 
                      `${tutor.firstName} ${tutor.lastName}` : 
                      tutor.name || 'Unknown Name'}
                  </h3>
                  <span 
                    className="employment-type"
                    style={{ backgroundColor: getEmploymentTypeColor(tutor.employment_type) }}
                  >
                    {getEmploymentTypeLabel(tutor.employment_type)}
                  </span>
                  <span className={`status ${tutor.is_active ? 'active' : 'inactive'}`}>
                    {tutor.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="tutor-details">
                  <p><strong>Email:</strong> {tutor.email}</p>
                  <p><strong>Phone:</strong> {tutor.phone || 'No phone'}</p>
                  <p><strong>TutorCruncher ID:</strong> <a 
                    href={`https://account.storytimechess.com/contractors/${tutor.contractorId}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tutor-id-link"
                  >
                    {tutor.contractorId}
                  </a></p>
                  <p><strong>Branch ID:</strong> {tutor.branchWorkerId || 'Not assigned'}</p>
                  
                  {earningsData[tutor.contractorId] && (
                    <div className="earnings-info">
                      <h4>Earnings ({selectedYear})</h4>
                      <p><strong>Total:</strong> {formatCurrency(earningsData[tutor.contractorId].totalEarnings)}</p>
                      <p><strong>Paid:</strong> {formatCurrency(earningsData[tutor.contractorId].paidEarnings)}</p>
                      <p><strong>Pending:</strong> {formatCurrency(earningsData[tutor.contractorId].pendingEarnings)}</p>
                      <p><strong>Orders:</strong> {earningsData[tutor.contractorId].totalOrders || 0}</p>
                    </div>
                  )}
                </div>

                <div className="tutor-actions">
                  <div className="action-buttons-grid">
                    <a
                      href={`https://app.tutorcruncher.com/contractors/${tutor.contractorId}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-btn view-tutor-btn"
                    >
                      👁️ View in TutorCruncher
                    </a>
                    
                    {/* WordPress Integration */}
                    {!tutor.wordpressUserId ? (
                      <button
                        onClick={() => handleCreateWordPressUser(tutor.contractorId)}
                        disabled={wordpressStatus[tutor.contractorId] === 'creating'}
                        className="action-btn wordpress-btn"
                      >
                        {wordpressStatus[tutor.contractorId] === 'creating' ? '🔄 Creating...' :
                         wordpressStatus[tutor.contractorId] === 'created' ? '✅ Created' :
                         wordpressStatus[tutor.contractorId] === 'failed' ? '❌ Failed' :
                         '🌐 Create WordPress User'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeleteWordPressUser(tutor.contractorId)}
                        disabled={wordpressStatus[tutor.contractorId] === 'deleting'}
                        className="action-btn wordpress-btn delete"
                      >
                        {wordpressStatus[tutor.contractorId] === 'deleting' ? '🔄 Deleting...' :
                         wordpressStatus[tutor.contractorId] === 'deleted' ? '✅ Deleted' :
                         wordpressStatus[tutor.contractorId] === 'failed' ? '❌ Failed' :
                         '🗑️ Delete WordPress User'}
                      </button>
                    )}

                    {/* Branch ID Management */}
                    <button
                      onClick={() => {
                        setSelectedContractorForBranch(tutor);
                        setShowBranchIdModal(true);
                      }}
                      className="action-btn branch-btn"
                    >
                      🔗 Manage Branch ID
                    </button>

                    {/* Google Groups Management */}
                    <button
                      onClick={() => handleManageGroups(tutor)}
                      className="action-btn groups-btn"
                    >
                      📧 Manage Google Groups
                    </button>
                    
                  </div>

                  {/* Current Groups Display */}
                  {tutor.googleGroupsMemberships && tutor.googleGroupsMemberships.length > 0 && (
                    <div className="current-groups-display">
                      <span className="groups-label">Current Groups:</span>
                      <div className="groups-badges">
                        {tutor.googleGroupsMemberships.map((membership, index) => (
                          <span key={index} className="group-badge">
                            {membership.groupName} ({membership.role})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}


                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Tutor Signup Modal */}
      {showSignupForm && (
        <div className="modal-overlay">
          <div className="modal-content signup-modal">
            <div className="modal-header">
              <h2>Add New Tutor</h2>
              <button onClick={() => setShowSignupForm(false)} className="close-btn">×</button>
            </div>
            
            <form onSubmit={handleSignupSubmit} className="signup-form">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      required
                      value={signupForm.firstName}
                      onChange={(e) => setSignupForm({...signupForm, firstName: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      required
                      value={signupForm.lastName}
                      onChange={(e) => setSignupForm({...signupForm, lastName: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      required
                      value={signupForm.email}
                      onChange={(e) => setSignupForm({...signupForm, email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      required
                      value={signupForm.phone}
                      onChange={(e) => setSignupForm({...signupForm, phone: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Employment Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Employment Type *</label>
                    <select
                      value={signupForm.employmentType}
                      onChange={(e) => setSignupForm({...signupForm, employmentType: e.target.value})}
                    >
                      <option value="1099">1099 Tutor</option>
                      <option value="w2">W2 Employee</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Hourly Rate *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={signupForm.hourlyRate}
                      onChange={(e) => setSignupForm({...signupForm, hourlyRate: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Onboarding Preferences</h3>
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={signupForm.onboardingPreferences.googleGroups}
                      onChange={(e) => setSignupForm({
                        ...signupForm, 
                        onboardingPreferences: {
                          ...signupForm.onboardingPreferences,
                          googleGroups: e.target.checked
                        }
                      })}
                    />
                    Add to Google Groups
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={signupForm.onboardingPreferences.storyTimeTutors}
                      onChange={(e) => setSignupForm({
                        ...signupForm, 
                        onboardingPreferences: {
                          ...signupForm.onboardingPreferences,
                          storyTimeTutors: e.target.checked
                        }
                      })}
                    />
                    Add to Story Time Tutors (WordPress)
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={signupForm.onboardingPreferences.branchPayments}
                      onChange={(e) => setSignupForm({
                        ...signupForm, 
                        onboardingPreferences: {
                          ...signupForm.onboardingPreferences,
                          branchPayments: e.target.checked
                        }
                      })}
                    />
                    Setup Branch Payments
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={signupForm.onboardingPreferences.tutorCruncher}
                      onChange={(e) => setSignupForm({
                        ...signupForm, 
                        onboardingPreferences: {
                          ...signupForm.onboardingPreferences,
                          tutorCruncher: e.target.checked
                        }
                      })}
                    />
                    Add to TutorCruncher
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowSignupForm(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Google Groups Management Modal */}
      {showGroupsModal && selectedTutorForGroups && (
        <div className="modal-overlay">
          <div className="modal-content groups-modal">
            <div className="modal-header">
              <h2>Manage Google Groups - {selectedTutorForGroups.name}</h2>
              <button onClick={() => setShowGroupsModal(false)} className="close-btn">×</button>
            </div>
            
            <div className="modal-body">
              <div className="groups-section">
                <h3>Available Groups</h3>
                <div className="groups-grid">
                  {Object.entries(availableGroups).map(([groupKey, groupInfo]) => {
                    const isMember = selectedTutorForGroups.googleGroupsMemberships?.some(
                      membership => membership.groupKey === groupKey
                    );
                    
                    return (
                      <div key={groupKey} className="group-item">
                        <div className="group-info">
                          <h4>{groupInfo.name}</h4>
                          <p>{groupInfo.email}</p>
                          <span className={`membership-status ${isMember ? 'member' : 'not-member'}`}>
                            {isMember ? '✅ Member' : '❌ Not a member'}
                          </span>
                        </div>
                        
                        <div className="group-actions">
                          {!isMember ? (
                            <button
                              onClick={() => handleAddToGroups(selectedTutorForGroups.email, [groupKey])}
                              disabled={groupsLoading}
                              className="add-to-group-btn"
                            >
                              ➕ Add to Group
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRemoveFromGroups(selectedTutorForGroups.email, [groupKey])}
                              disabled={groupsLoading}
                              className="remove-from-group-btn"
                            >
                              ➖ Remove from Group
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="modal-actions">
                <button onClick={() => setShowGroupsModal(false)} className="close-modal-btn">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Branch ID Management Modal */}
      {showBranchIdModal && (
        <div className="modal-overlay">
          <div className="modal-content branch-id-modal">
            <div className="modal-header">
              <h2>Add Branch ID</h2>
              <button onClick={() => setShowBranchIdModal(false)} className="close-btn">×</button>
            </div>
            
            <div className="modal-body">
              {branchMessage && (
                <div className={`message ${branchMessage.includes('successfully') ? 'success' : 'error'}`}>
                  {branchMessage}
                </div>
              )}

              <div className="branch-id-summary">
                <div className="summary-stats">
                  <span className="stat-item">
                    <strong>{contractorsWithoutBranchIds.length}</strong> tutors need Branch IDs
                  </span>
                  <span className="stat-item">
                    <strong>{contractorsWithBranchIds.length}</strong> already have Branch IDs
                  </span>
                </div>
              </div>

              <div className="search-section">
                <h3>🔍 Find Tutor</h3>
                <div className="search-form">
                  <input
                    type="text"
                    value={branchSearchTerm}
                    onChange={(e) => setBranchSearchTerm(e.target.value)}
                    placeholder="Search by name, email, or ID..."
                    onKeyDown={(e) => e.key === 'Enter' && searchContractorsForBranch()}
                  />
                  <button onClick={searchContractorsForBranch} className="search-btn">
                    Search
                  </button>
                </div>
                
                {branchSearchResults.length > 0 && (
                  <div className="search-results">
                    <h4>Search Results</h4>
                    {branchSearchResults.map(contractor => (
                      <div key={contractor.contractorId} className="contractor-item">
                        <div className="contractor-info">
                          <h3>{contractor.firstName} {contractor.lastName}</h3>
                          <p>{contractor.email}</p>
                          {contractor.branchWorkerId ? (
                            <span className="branch-id">Branch ID: {contractor.branchWorkerId}</span>
                          ) : (
                            <span className="missing-id">No Branch ID</span>
                          )}
                        </div>
                        {!contractor.branchWorkerId && (
                          <button 
                            onClick={() => {
                              setSelectedContractorForBranch(contractor);
                            }}
                            className="add-btn"
                          >
                            Add ID
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="add-branch-id-form">
                <h3>➕ Add Branch ID</h3>
                <div className="form-group">
                  <label>Contractor:</label>
                  <select 
                    value={selectedContractorForBranch?.contractorId || ''} 
                    onChange={(e) => {
                      const contractor = [...contractorsWithoutBranchIds, ...branchSearchResults]
                        .find(c => c.contractorId === e.target.value);
                      setSelectedContractorForBranch(contractor || null);
                    }}
                    required
                  >
                    <option value="">Select a contractor without Branch ID...</option>
                    {[...contractorsWithoutBranchIds, ...branchSearchResults].map(contractor => (
                      <option key={contractor.contractorId} value={contractor.contractorId}>
                        {contractor.firstName} {contractor.lastName} ({contractor.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Branch Worker ID:</label>
                  <div className="input-with-button">
                    <input
                      type="text"
                      value={newBranchId}
                      onChange={(e) => setNewBranchId(e.target.value)}
                      placeholder="Enter Branch worker ID..."
                    />
                    <button 
                      type="button"
                      onClick={generateBranchWorkerId}
                      className="generate-id-btn"
                      title="Generate a random Branch Worker ID"
                    >
                      🎲 Generate ID
                    </button>
                  </div>
                  <small className="form-help">
                    Click "Generate ID" to create a random Branch Worker ID in the correct format
                  </small>
                </div>
                
                <div className="form-actions">
                  <button onClick={addBranchId} className="add-btn" disabled={!selectedContractorForBranch || !newBranchId.trim()}>
                    Add Branch ID
                  </button>
                </div>
              </div>

              <div className="quick-actions">
                <h3>Quick Actions</h3>
                <div className="action-buttons">
                  <button onClick={generateMissingIdsCSV} className="csv-btn">
                    📊 Download Missing IDs CSV
                  </button>
                  <button 
                    onClick={() => {
                      if (contractorsWithoutBranchIds.length > 0) {
                        setSelectedContractorForBranch(contractorsWithoutBranchIds[0]);
                      }
                    }} 
                    className="quick-add-btn"
                    disabled={contractorsWithoutBranchIds.length === 0}
                  >
                    🚀 Quick Add (First Missing)
                  </button>
                </div>
              </div>
              
              <div className="modal-actions">
                <button onClick={() => setShowBranchIdModal(false)} className="close-modal-btn">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">{toast.type === 'success' ? '✓' : '✕'}</span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default TutorManagement; 